// Board layouts — single source of truth for client, worker, and agent.
//
// Active layouts are 14×14 (coords 0-13). The board has no single center cell;
// the four middle cells (rows/cols 6-7) form a 2×2 center zone, and the first
// word must cover any one of them. Layouts never place bonus squares on the
// center zone (the center gives no bonus, by rule).
//
// Legacy 13×13 layouts are kept (under their original names) so saved and
// resumed games render, validate, and score exactly as they were created.
// New games only ever select from the active set.

export const DEFAULT_BOARD_SIZE = 14;

// Active 14×14 layouts — one is randomly selected per game
export const BOARD_LAYOUTS = {
    "Halo": {
        size: 14,
        squares: {
            tripleWord: [[0,0], [0,13], [13,0], [13,13]],
            doubleWord: [[2,2], [2,11], [11,2], [11,11], [5,6], [5,7], [8,6], [8,7]],
            tripleLetter: [[0,6], [0,7], [6,0], [7,0], [6,13], [7,13], [13,6], [13,7], [0,4], [0,9], [4,0], [9,0], [4,13], [9,13], [13,4], [13,9]],
            doubleLetter: [[1,5], [1,8], [5,1], [8,1], [5,12], [8,12], [12,5], [12,8], [3,3], [3,10], [10,3], [10,10], [4,6], [4,7], [6,4], [7,4], [6,9], [7,9], [9,6], [9,7]]
        }
    },
    "Compass": {
        size: 14,
        squares: {
            tripleWord: [[0,6], [6,13], [13,7], [7,0]],
            doubleWord: [[0,0], [0,13], [13,0], [13,13], [3,6], [6,10], [10,7], [7,3]],
            tripleLetter: [[2,2], [2,11], [11,2], [11,11], [4,4], [4,9], [9,4], [9,9]],
            doubleLetter: [[1,3], [1,10], [3,1], [10,1], [3,12], [10,12], [12,3], [12,10], [5,5], [5,8], [8,5], [8,8]]
        }
    },
    "Open Field": {
        size: 14,
        squares: {
            tripleWord: [[0,0], [0,13], [13,0], [13,13]],
            doubleWord: [[2,2], [2,11], [11,2], [11,11]],
            tripleLetter: [[0,4], [0,9], [4,0], [9,0], [4,13], [9,13], [13,4], [13,9]],
            doubleLetter: [[1,1], [1,12], [12,1], [12,12], [4,4], [4,9], [9,4], [9,9]]
        }
    },
    "Crosshair": {
        size: 14,
        squares: {
            tripleWord: [[0,6], [6,13], [13,7], [7,0]],
            doubleWord: [[0,0], [0,13], [13,0], [13,13], [3,6], [3,7], [10,6], [10,7]],
            tripleLetter: [[0,3], [0,10], [3,0], [10,0], [3,13], [10,13], [13,3], [13,10]],
            doubleLetter: [[3,3], [3,10], [10,3], [10,10], [4,6], [4,7], [6,4], [7,4], [6,9], [7,9], [9,6], [9,7]]
        }
    },
    "Zen Garden": {
        size: 14,
        squares: {
            tripleWord: [[0,0], [0,13], [13,0], [13,13]],
            doubleWord: [[3,3], [3,10], [10,3], [10,10]],
            tripleLetter: [[0,6], [6,13], [13,7], [7,0]],
            doubleLetter: [[2,6], [6,11], [11,7], [7,2], [4,4], [4,9], [9,4], [9,9]]
        }
    },
    "Confetti": {
        size: 14,
        squares: {
            tripleWord: [[1,1], [1,12], [12,1], [12,12]],
            doubleWord: [[0,5], [0,8], [5,0], [8,0], [5,13], [8,13], [13,5], [13,8]],
            tripleLetter: [[3,6], [6,10], [10,7], [7,3], [0,0], [0,13], [13,0], [13,13]],
            doubleLetter: [[2,4], [2,9], [4,2], [9,2], [4,11], [9,11], [11,4], [11,9], [5,5], [5,8], [8,5], [8,8]]
        }
    },
    "Honeycomb": {
        size: 14,
        squares: {
            tripleWord: [[0,3], [0,10], [3,0], [10,0], [3,13], [10,13], [13,3], [13,10]],
            doubleWord: [[2,6], [6,11], [11,7], [7,2], [4,4], [4,9], [9,4], [9,9]],
            tripleLetter: [[0,6], [6,13], [13,7], [7,0]],
            doubleLetter: [[3,3], [3,10], [10,3], [10,10], [5,6], [5,7], [8,6], [8,7]]
        }
    }
};

// Legacy 13×13 layouts — for loading games created before the board expansion
export const LEGACY_BOARD_LAYOUTS = {
    "Ring Pattern": {
        size: 13,
        squares: {
            tripleWord: [[0,0], [0,12], [12,0], [12,12]],
            doubleWord: [[2,2], [2,10], [10,2], [10,10], [5,6], [6,5], [6,7], [7,6]],
            tripleLetter: [[0,6], [6,0], [6,12], [12,6], [0,4], [0,8], [4,0], [8,0], [4,12], [8,12], [12,4], [12,8]],
            doubleLetter: [[1,5], [1,7], [5,1], [7,1], [5,11], [7,11], [11,5], [11,7], [3,3], [3,9], [9,3], [9,9], [4,6], [6,4], [6,8], [8,6]]
        }
    },
    "Diamond": {
        size: 13,
        squares: {
            tripleWord: [[0,6], [6,0], [6,12], [12,6]],
            doubleWord: [[0,0], [0,12], [12,0], [12,12], [3,6], [6,3], [6,9], [9,6]],
            tripleLetter: [[2,2], [2,10], [10,2], [10,10], [4,4], [4,8], [8,4], [8,8]],
            doubleLetter: [[1,3], [1,9], [3,1], [9,1], [3,11], [9,11], [11,3], [11,9], [5,5], [5,7], [7,5], [7,7]]
        }
    },
    "Sparse Corners": {
        size: 13,
        squares: {
            tripleWord: [[0,0], [0,12], [12,0], [12,12]],
            doubleWord: [[2,2], [2,10], [10,2], [10,10]],
            tripleLetter: [[0,4], [0,8], [4,0], [8,0], [4,12], [8,12], [12,4], [12,8]],
            doubleLetter: [[1,1], [1,11], [11,1], [11,11], [4,4], [4,8], [8,4], [8,8]]
        }
    },
    "Cross": {
        size: 13,
        squares: {
            tripleWord: [[0,6], [6,0], [6,12], [12,6]],
            doubleWord: [[3,6], [6,3], [6,9], [9,6], [0,0], [0,12], [12,0], [12,12]],
            tripleLetter: [[0,3], [0,9], [3,0], [9,0], [3,12], [9,12], [12,3], [12,9]],
            doubleLetter: [[3,3], [3,9], [9,3], [9,9], [4,6], [6,4], [6,8], [8,6]]
        }
    },
    "Minimal": {
        size: 13,
        squares: {
            tripleWord: [[0,0], [0,12], [12,0], [12,12]],
            doubleWord: [[3,3], [3,9], [9,3], [9,9]],
            tripleLetter: [[0,6], [6,0], [6,12], [12,6]],
            doubleLetter: [[2,6], [6,2], [6,10], [10,6], [4,4], [4,8], [8,4], [8,8]]
        }
    },
    "Scattered": {
        size: 13,
        squares: {
            tripleWord: [[1,1], [1,11], [11,1], [11,11]],
            doubleWord: [[0,5], [0,7], [5,0], [7,0], [5,12], [7,12], [12,5], [12,7]],
            tripleLetter: [[3,6], [6,3], [6,9], [9,6], [0,0], [0,12], [12,0], [12,12]],
            doubleLetter: [[2,4], [2,8], [4,2], [8,2], [4,10], [8,10], [10,4], [10,8], [5,5], [5,7], [7,5], [7,7]]
        }
    },
    "Hexagonal": {
        size: 13,
        squares: {
            tripleWord: [[0,3], [0,9], [3,0], [9,0], [3,12], [9,12], [12,3], [12,9]],
            doubleWord: [[2,6], [6,2], [6,10], [10,6], [4,4], [4,8], [8,4], [8,8]],
            tripleLetter: [[0,6], [6,0], [6,12], [12,6]],
            doubleLetter: [[3,3], [3,9], [9,3], [9,9], [5,6], [6,5], [6,7], [7,6]]
        }
    }
};

export const ALL_BOARD_LAYOUTS = { ...LEGACY_BOARD_LAYOUTS, ...BOARD_LAYOUTS };

// Look up a layout by name. Unknown/missing names fall back to "Ring Pattern"
// (the historical fallback — games without layoutName predate the 14×14 board).
export function getLayout(name) {
    return ALL_BOARD_LAYOUTS[name] || LEGACY_BOARD_LAYOUTS["Ring Pattern"];
}

// Random layout for a new game — active (14×14) layouts only
export function getRandomLayoutName() {
    const names = Object.keys(BOARD_LAYOUTS);
    return names[Math.floor(Math.random() * names.length)];
}

// Center cells for a board size: odd sizes have a single center cell,
// even sizes a 2×2 center zone. First word must cover any one of them.
export function getCenterCells(size) {
    const lo = Math.floor((size - 1) / 2);
    const hi = Math.ceil((size - 1) / 2);
    const cells = [];
    for (let r = lo; r <= hi; r++) {
        for (let c = lo; c <= hi; c++) {
            cells.push([r, c]);
        }
    }
    return cells;
}

export function isCenterCell(row, col, size) {
    const lo = Math.floor((size - 1) / 2);
    const hi = Math.ceil((size - 1) / 2);
    return row >= lo && row <= hi && col >= lo && col <= hi;
}
